/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { Avail } from '../../model/AdMetadata';

export const TIME_THRESHOLD = 0.001;

function compare(first: number, second: number): boolean {
  if (first < second) {
    return true;
  }

  return Math.abs(first - second) < TIME_THRESHOLD;
}

/**
 * Returns a boolean value indicating whethere there are promos available or not.
 * @param adBreaks the list of ad breaks
 */
export function isAdBreaksAvailable(adBreaks: Avail[] | undefined): boolean {
  return !!(adBreaks && adBreaks.length);
}

/**
 * Returns the total duration of all provided ad breaks.
 * @param adBreaks the list of ad breaks
 */
export function getTotalAdvertsBreaksDuration(adBreaks: Avail[]): number {
  if (!adBreaks.length) {
    return 0;
  }
  return adBreaks
    .map((adBreak: Avail) => adBreak?.durationInSeconds ?? 0)
    .reduce((accumulator: number, currentValue: number) => accumulator + currentValue, 0);
}

export function currentAdBreakCompletedAdsDuration(playhead: number, adBreak: Avail): number {
  if (!adBreak || !adBreak.ads || !adBreak.ads.length) {
    return 0;
  }

  const filteredAd = adBreak.ads
    .filter(
      ad =>
        compare(ad?.startTimeInSeconds ?? 0, playhead) &&
        playhead < (ad?.startTimeInSeconds ?? 0) + (ad.durationInSeconds ?? 0),
    )
    .shift();

  return (filteredAd?.startTimeInSeconds ?? 0) + (filteredAd?.durationInSeconds ?? 0);
}

/**
 * Returns the total duration of all provided ad breaks of which start time
 * is before the current time position.
 * @param currentTime the current time position.
 * @param adBreaks the list of ad breaks
 */
export function getAdvertBreaksDurationPriorTo(currentTime: number, adBreaks: Avail[]): number {
  return adBreaks
    .filter((adBreak: Avail) => (adBreak?.startTimeInSeconds ?? 0) < currentTime)
    .map((adBreak: Avail) => adBreak?.durationInSeconds ?? 0)
    .reduce((accumulator: number, currentValue: number) => accumulator + currentValue, 0);
}

/**
 * Returns a list of all active ad breaks of which start time is between the given range.
 * @param startPosition the start position.
 * @param endPosition the end position.
 * @param adBreaks the list of ad breaks
 */
export function getAdBreaksBetween(startPosition: number, endPosition: number, adBreaks: Avail[]): Avail[] {
  return adBreaks.filter(
    (adBreak: Avail) =>
      (adBreak?.startTimeInSeconds ?? 0) >= startPosition &&
      (adBreak?.startTimeInSeconds ?? 0) <= endPosition &&
      (adBreak?.durationInSeconds ?? 0) > 0,
  );
}

/**
 * Returns the absolute position to seek for given a relative position. To calculate the absolute
 * position it calculates the total duration of all preroll ad breaks and all other breaks of which
 * start time is before the current time position.
 * @param playhead the content time position.
 * @param adBreaks the list of ad breaks
 */
export function getSeekPositionFor(playhead: number, adBreaks: Avail[], streamDuration = 0): number {
  // Prevent seek to negative playhead positions
  if (playhead < 0) {
    playhead = 0;
  }

  let streamPlayhead = streamTimeForContentTime(playhead, streamDuration, adBreaks ?? []);

  const adBreakNextToSeekPosition = adBreaks.find((adBreak: Avail) => (adBreak.startTimeInSeconds ?? 0) >= playhead);

  if (
    adBreakNextToSeekPosition &&
    streamPlayhead > (adBreakNextToSeekPosition?.startTimeInSeconds ?? 0) &&
    streamPlayhead <
      (adBreakNextToSeekPosition?.startTimeInSeconds ?? 0) + (adBreakNextToSeekPosition?.durationInSeconds ?? 0)
  ) {
    const adBreakEndInContentTime =
      (adBreakNextToSeekPosition?.startTimeInSeconds ?? 0) + (adBreakNextToSeekPosition?.durationInSeconds ?? 0);
    streamPlayhead = streamTimeForContentTime(adBreakEndInContentTime, streamDuration, adBreaks ?? []);
  }
  return streamPlayhead;
}

export function streamTimeForContentTime(contentTime: number, streamDuration: number, adBreaks: Avail[]): number {
  // Handle negative content time by returning preroll duration or 0
  if (contentTime < 0) {
    const preroll = adBreaks.find(adBreak => adBreak.startTimeInSeconds === 0);
    return preroll ? (preroll?.durationInSeconds ?? 0) : 0;
  }

  // Include a zero-duration ad break at the start if no preroll exists
  const internalAdBreaks = adBreaks.some(adBreak => adBreak.startTimeInSeconds === 0)
    ? adBreaks
    : [{ startTimeInSeconds: 0, durationInSeconds: 0 }, ...adBreaks];

  // Calculate the total duration of all ad breaks
  const totalAdBreaksDuration = internalAdBreaks.reduce((sum, adBreak) => sum + (adBreak.durationInSeconds ?? 0), 0);

  // Check if the content time is beyond the last ad break, return the stream duration
  if (contentTime >= streamDuration - totalAdBreaksDuration) {
    return streamDuration;
  }

  // Iterate through the ad breaks to adjust the content time
  for (let i = 0; i < internalAdBreaks.length; i++) {
    const currentAdEnd = (internalAdBreaks[i]?.startTimeInSeconds ?? 0) + (internalAdBreaks[i]?.durationInSeconds ?? 0);
    const nextAdStart = internalAdBreaks[i + 1]?.startTimeInSeconds || (isNaN(streamDuration) ? 0 : streamDuration);
    // Time between current ad break end and next ad break start
    const gap = nextAdStart - currentAdEnd;

    // If remaining content time fits within the gap, return the corresponding stream time
    if (contentTime <= gap) {
      return currentAdEnd + contentTime;
    }

    // Subtract the gap from the content time and continue
    contentTime -= gap;
  }

  // Return the original content time if all adjustments are processed
  return contentTime;
}
